import type { ComponentType, SVGProps } from 'react'
import { ChatBubble, Community, PageEdit as PageEditIcon, CalendarPlus, UploadSquare } from 'iconoir-react'
import { DateComponent } from 'app/components/DateComponent'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { isSMCOrg } from 'lib/isSMCOrg'

export const fileImportColumns = (orgId?: string) => {
  const isSMC = isSMCOrg(orgId)

  const baseColumns = [
    {
      id: `Workspace`,
      header: `Workspace`,
      accessorKey: `workspaceId`,
      enableSorting: false,
      accessorFn: (row) => row.workspaceId,
      cell: (props) => <LazyWorkspaceCell workspaceId={props.getValue()} />,
      meta: { defaultVisible: true },
    },
    {
      id: `Location`,
      header: `Location`,
      accessorKey: `locationId`,
      enableSorting: false,
      cell: (props) => <LazyLocationCell locationId={props.getValue()} />,
      meta: { defaultVisible: true },
    },
    {
      id: `activityVendor`,
      header: `Activity Vendor`,
      accessorFn: (row) => row.vendor,
      meta: { defaultVisible: true, sortKey: `vendor.keyword` },
    },
    {
      id: `type`,
      header: `Activity Type`,
      accessorFn: (row) => row.type,
      cell: (props) => {
        let Icon: ComponentType<SVGProps<SVGSVGElement>>
        switch (props.getValue()) {
          case `Chat`:
            Icon = ChatBubble
            break
          case `Form`:
            Icon = PageEditIcon
            break
          case `Online Scheduler`:
            Icon = CalendarPlus
            break
          case `Virtual Consultation`:
            Icon = Community
            break
          default:
            Icon = UploadSquare
            break
        }
        return (
          <div className="flex gap-x-2 align-middle">
            <Icon className="h-4 w-4 shrink-0" />
            {props.getValue()}
          </div>
        )
      },
      meta: { defaultVisible: true, sortKey: `type.keyword` },
    },
    {
      id: `name`,
      header: `Name`,
      accessorFn: (row) => row.name,
      meta: { defaultVisible: true, sortKey: `name.keyword` },
    },
    {
      id: `activityDate`,
      header: `Activity Date`,
      accessorFn: (row) => row.activityDate,
      cell: (props) => <DateComponent value={props.getValue()} format="MM/DD/YYYY" dateOnly />,
      meta: { defaultVisible: true, sortKey: `activityDate` },
    },
    {
      id: `requestedAppointmentTime`,
      header: `Requested Appointment`,
      accessorFn: (row) => row.requestedAppointmentTime,
      cell: (props) => <DateComponent value={props.getValue()} format="MM/DD/YYYY" dateOnly />,
      meta: { defaultVisible: true, sortKey: `requestedAppointmentTime` },
    },
    {
      id: `phone`,
      header: `Phone`,
      accessorFn: (row) => row.phone,
      cell: (props) => <PhoneNumber number={props.getValue()} />,
      meta: { defaultVisible: true, sortKey: `phone` },
    },
    {
      id: `email`,
      header: `email`,
      accessorFn: (row) => row.email,
      meta: { defaultVisible: true, sortKey: `email` },
    },
    {
      id: `scheduled`,
      header: `Scheduled?`,
      accessorFn: (row) => (row.isScheduled ? `Yes` : `No`),
      meta: { defaultVisible: true, sortKey: `isScheduled` },
    },
    {
      id: `requestedTreatment`,
      header: `Requested Treatment`,
      accessorFn: (row) => row.treatment,
      meta: { defaultVisible: true, sortKey: `treatment` },
    },
    {
      id: `leadOwner`,
      header: `Contact Owner`,
      accessorFn: (row) => row.source,
      meta: { defaultVisible: true, sortKey: `source.keyword` },
    },
  ]

  if (isSMC) {
    return [
      ...baseColumns,
      {
        id: `channel`,
        header: `Channel`,
        accessorFn: (row) => row.channel,
        meta: { defaultVisible: true, sortKey: `channel.keyword` },
      },
    ]
  }

  return [
    ...baseColumns,
    {
      id: `utmSource`,
      header: `Source`,
      accessorFn: (row) => row.utmSource,
      meta: { defaultVisible: true, sortKey: `utmSource.keyword` },
    },
    {
      id: `utmMedium`,
      header: `Medium`,
      accessorFn: (row) => row.utmMedium,
      meta: { defaultVisible: true, sortKey: `utmMedium.keyword` },
    },
    {
      id: `utmCampaign`,
      header: `Campaign`,
      accessorFn: (row) => row.utmCampaign,
      meta: { defaultVisible: true, sortKey: `utmCampaign.keyword` },
    },
  ]
}
